/**
 * Accessibility Features
 * Screen reader announcements and keyboard navigation support
 */

/**
 * Announce message to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - Priority level ('polite' or 'assertive')
 */
export function announce(message, priority = 'polite') {
    const announcer = document.getElementById('announcer');
    if (!announcer) {
        console.warn('Announcer element not found');
        return;
    }

    // Set aria-live priority
    announcer.setAttribute('aria-live', priority);

    // Clear and set new message
    announcer.textContent = '';
    setTimeout(() => {
        announcer.textContent = message;
    }, 100);
}

/**
 * Announce progress update
 * @param {number} completed - Number of completed items
 * @param {number} total - Total number of items
 * @param {string} context - Context description (e.g., "pairs matched")
 */
export function announceProgress(completed, total, context = 'items') {
    announce(`${completed} of ${total} ${context}`);
}

/**
 * Announce success
 * @param {string} message - Success message
 */
export function announceSuccess(message) {
    announce(`Success! ${message}`, 'assertive');
}

/**
 * Announce error
 * @param {string} message - Error message
 */
export function announceError(message) {
    announce(`Error: ${message}`, 'assertive');
}

/**
 * Set up keyboard navigation for a container
 * @param {HTMLElement} container - Container element
 * @param {Array<HTMLElement>} focusableElements - Array of focusable elements
 * @param {Object} options - Configuration options
 */
export class KeyboardNavigator {
    constructor(container, focusableElements, options = {}) {
        this.container = container;
        this.elements = focusableElements;
        this.currentIndex = 0;
        this.onSelect = options.onSelect || (() => {});
        this.onEscape = options.onEscape || (() => {});
        this.wrap = options.wrap !== false; // Default to true

        this.boundHandleKeyDown = this.handleKeyDown.bind(this);
        this.init();
    }

    init() {
        this.container.addEventListener('keydown', this.boundHandleKeyDown);

        // Set initial focus if elements exist
        if (this.elements.length > 0) {
            this.elements[0].setAttribute('tabindex', '0');
        }
    }

    handleKeyDown(event) {
        const key = event.key;

        switch (key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                this.moveNext();
                break;

            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                this.movePrevious();
                break;

            case 'Enter':
            case ' ':
                event.preventDefault();
                this.selectCurrent();
                break;

            case 'Escape':
                event.preventDefault();
                this.onEscape();
                break;

            case 'Home':
                event.preventDefault();
                this.moveToFirst();
                break;

            case 'End':
                event.preventDefault();
                this.moveToLast();
                break;
        }
    }

    moveNext() {
        if (this.elements.length === 0) return;

        let nextIndex = this.currentIndex + 1;
        if (nextIndex >= this.elements.length) {
            nextIndex = this.wrap ? 0 : this.elements.length - 1;
        }

        this.moveTo(nextIndex);
    }

    movePrevious() {
        if (this.elements.length === 0) return;

        let prevIndex = this.currentIndex - 1;
        if (prevIndex < 0) {
            prevIndex = this.wrap ? this.elements.length - 1 : 0;
        }

        this.moveTo(prevIndex);
    }

    moveToFirst() {
        if (this.elements.length > 0) {
            this.moveTo(0);
        }
    }

    moveToLast() {
        if (this.elements.length > 0) {
            this.moveTo(this.elements.length - 1);
        }
    }

    moveTo(index) {
        if (index < 0 || index >= this.elements.length) return;

        // Update tabindex
        this.elements[this.currentIndex]?.setAttribute('tabindex', '-1');
        this.elements[index].setAttribute('tabindex', '0');
        this.elements[index].focus();

        this.currentIndex = index;
    }

    selectCurrent() {
        if (this.elements[this.currentIndex]) {
            this.onSelect(this.currentIndex, this.elements[this.currentIndex]);
        }
    }

    update(newElements) {
        this.elements = newElements;
        this.currentIndex = Math.min(this.currentIndex, this.elements.length - 1);

        // Update tabindex on all elements
        this.elements.forEach((el, i) => {
            el.setAttribute('tabindex', i === this.currentIndex ? '0' : '-1');
        });
    }

    destroy() {
        this.container.removeEventListener('keydown', this.boundHandleKeyDown);
    }
}

/**
 * Add focus indicator styles if not already present
 */
export function ensureFocusIndicators() {
    // Check if styles already exist
    if (document.getElementById('a11y-focus-styles')) {
        return;
    }

    const style = document.createElement('style');
    style.id = 'a11y-focus-styles';
    style.textContent = `
        /* Keyboard focus indicators */
        *:focus-visible {
            outline: 3px solid var(--border-focus, #2196f3);
            outline-offset: 2px;
        }

        .word-button:focus-visible {
            outline: 3px solid var(--border-focus, #2196f3);
            outline-offset: 2px;
        }

        /* Skip to content link */
        .skip-link {
            position: absolute;
            top: -40px;
            left: 0;
            background: var(--primary-color, #2196f3);
            color: white;
            padding: 8px;
            text-decoration: none;
            z-index: 10000;
        }

        .skip-link:focus {
            top: 0;
        }
    `;
    document.head.appendChild(style);
}
